/** Shared reference-data queries used across screens. */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase, unwrap } from '@/lib/supabase';

export const qk = {
  crops: ['crops'] as const,
  farm: ['farm_settings'] as const,
  greenhouses: ['greenhouses'] as const,
  stations: ['stations'] as const,
  clients: ['clients'] as const,
  suppliers: ['suppliers'] as const,
  employees: ['employees'] as const,
  team: ['team'] as const,
  totals: (from: string, to: string) => ['daily_crop_totals', from, to] as const,
};

const REF = { staleTime: 5 * 60_000 };

export const useCrops = () =>
  useQuery({
    ...REF,
    queryKey: qk.crops,
    queryFn: async () => unwrap(await supabase.from('crops').select('*').order('sort')),
  });

export const useFarmSettings = () =>
  useQuery({
    ...REF,
    queryKey: qk.farm,
    queryFn: async () =>
      unwrap(await supabase.from('farm_settings').select('*').eq('id', 1).single()),
  });

/** Pack weight per product; products without their own weight use the farm default. */
/** Beta: AI features (farm-wide switch, admin-only). Off until the settings have loaded. */
export const useBetaAi = () => useFarmSettings().data?.beta_ai === true;

export function usePackKgFor() {
  const crops = useCrops().data;
  const farm = Number(useFarmSettings().data?.pack_kg ?? 0.115);
  return useCallback(
    (code: string) => Number(crops?.find((c) => c.code === code)?.pack_kg ?? farm),
    [crops, farm],
  );
}

export const useGreenhouses = () =>
  useQuery({
    ...REF,
    queryKey: qk.greenhouses,
    queryFn: async () =>
      unwrap(await supabase.from('greenhouses').select('*, greenhouse_bays(*)').order('no')),
  });

export const useStations = () =>
  useQuery({
    ...REF,
    queryKey: qk.stations,
    queryFn: async () => unwrap(await supabase.from('stations').select('*').order('sort')),
  });

export const useClients = () =>
  useQuery({
    ...REF,
    queryKey: qk.clients,
    queryFn: async () => unwrap(await supabase.from('clients').select('*').order('name')),
  });

export const useSuppliers = () =>
  useQuery({
    ...REF,
    queryKey: qk.suppliers,
    queryFn: async () => unwrap(await supabase.from('suppliers').select('*').order('name')),
  });

export const useEmployees = (enabled = true) =>
  useQuery({
    ...REF,
    enabled,
    queryKey: qk.employees,
    queryFn: async () => unwrap(await supabase.from('employees').select('*').order('first_name')),
  });

export const useTeam = () =>
  useQuery({
    ...REF,
    queryKey: qk.team,
    queryFn: async () => unwrap(await supabase.rpc('team_directory')),
  });

/** Active products the farm grows itself (not bought-in "other" items). */
export function useFarmCrops() {
  const crops = useCrops().data;
  return useMemo(() => (crops ?? []).filter((c) => c.active && !c.is_other), [crops]);
}

/** Crop code → name in the current language, with the Hebrew name as fallback. */
export function useCropName() {
  const { i18n } = useTranslation();
  const crops = useCrops().data;
  const lang = i18n.language;
  return useCallback(
    (code: string) => {
      const c = crops?.find((x) => x.code === code);
      if (!c) return code;
      return (lang === 'th' ? c.name_th : lang === 'en' ? c.name_en : c.name_he) || c.name_he;
    },
    [crops, lang],
  );
}
